import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { UserTracksResponse } from '@verybigthings/trackframe-shared/schemas';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { hub } from '@/lib/hub-client';
import { LevelCell, RoleChips } from '../tracks/access/grant-fields';
import { accessErrorMessage, removeUserFromTrack } from '../tracks/access/remove-user';

type UserTrack = UserTracksResponse['items'][number];

/**
 * Tracks tab of the user page (HUB-109 D7): every track the user enters by
 * level or role. Remove reuses the Access tab's sequence — the user's exact
 * held relations come from the track's users list, so nothing is left behind.
 */
export function UserTracksTab({ userId }: { userId: string }) {
  const [items, setItems] = useState<UserTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<UserTrack | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await hub.usersTracksList({ params: { id: userId } });
      setItems(res.items);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const confirmRemove = async () => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      const users = await hub.objectsUsersList({ params: { objectType: 'track', objectId: pending.trackId } });
      const row = users.items.find((item) => item.userId === userId);
      if (row) await removeUserFromTrack(pending.trackId, row);
      await load();
      setPending(null);
    } catch (err) {
      setError(accessErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Tracks</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <p className="text-sm text-destructive">{error}</p>}
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">This user has no track access.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Track</TableHead>
                <TableHead>Level</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead className="w-[1%]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.trackId}>
                  <TableCell>
                    <Link to={`/admin/tracks/show/${item.trackId}/access`} className="underline underline-offset-2">
                      {item.displayName?.trim() || item.trackName}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <LevelCell level={item.level} />
                  </TableCell>
                  <TableCell>
                    <RoleChips roles={item.roles} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => setPending(item)}>
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <ConfirmDialog
        open={pending !== null}
        title="Remove from track?"
        description={`The user loses every level and every role on ${pending?.displayName?.trim() || pending?.trackName || ''}.`}
        confirmLabel="Remove"
        destructive
        busy={busy}
        onConfirm={() => void confirmRemove()}
        onCancel={() => setPending(null)}
      />
    </Card>
  );
}
